import { useEffect, useState } from "react";
import api, { formatEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Target, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [current, setCurrent] = useState("");
  const [deadline, setDeadline] = useState("");

  const load = async () => {
    const { data } = await api.get("/goals");
    setGoals(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!name || !target) return toast.error("Nom et objectif requis");
    await api.post("/goals", {
      name,
      target_amount: parseFloat(target),
      current_amount: parseFloat(current) || 0,
      deadline: deadline || null,
      color: "#3B82F6",
    });
    toast.success("Objectif créé");
    setOpen(false);
    setName(""); setTarget(""); setCurrent(""); setDeadline("");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/goals/${id}`);
    load();
  };

  const addFunds = async (g, amt) => {
    await api.put(`/goals/${g.id}`, {
      name: g.name, target_amount: g.target_amount,
      current_amount: (g.current_amount || 0) + amt,
      deadline: g.deadline, color: g.color,
    });
    toast.success("Progrès mis à jour");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Objectifs</h1>
          <p className="text-stone-500 mt-1">Vos rêves, quantifiés</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="btn-add-goal">
              <Plus className="w-4 h-4 mr-1" /> Objectif
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nouvel objectif</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nom</Label><Input value={name} onChange={(e) => setName(e.target.value)}
                className="rounded-xl h-11 mt-1.5" placeholder="Ex: Vacances Italie" data-testid="goal-name-input" /></div>
              <div><Label>Montant cible (€)</Label><Input type="number" step="0.01" value={target}
                onChange={(e) => setTarget(e.target.value)} className="rounded-xl h-11 mt-1.5" data-testid="goal-target-input" /></div>
              <div><Label>Déjà épargné (€)</Label><Input type="number" step="0.01" value={current}
                onChange={(e) => setCurrent(e.target.value)} className="rounded-xl h-11 mt-1.5" data-testid="goal-current-input" /></div>
              <div><Label>Date cible (optionnel)</Label><Input type="date" value={deadline}
                onChange={(e) => setDeadline(e.target.value)} className="rounded-xl h-11 mt-1.5" data-testid="goal-deadline-input" /></div>
              <Button onClick={submit} className="w-full h-11 rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="goal-submit-button">
                Créer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {goals.length === 0 ? (
        <Card className="p-12 rounded-2xl border-stone-200 text-center">
          <Target className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-400">Aucun objectif défini.</p>
          <p className="text-stone-500 text-sm mt-2">Créez un objectif d'épargne pour vos projets.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map((g) => {
            const pct = Math.min(100, ((g.current_amount || 0) / g.target_amount) * 100);
            return (
              <Card key={g.id} className="p-6 rounded-2xl border-stone-200" data-testid={`goal-${g.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Target className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold">{g.name}</div>
                      {g.deadline && <div className="text-xs text-stone-500">Cible: {new Date(g.deadline).toLocaleDateString("fr-FR")}</div>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(g.id)} data-testid={`goal-delete-${g.id}`}>
                    <Trash2 className="w-4 h-4 text-stone-400" />
                  </Button>
                </div>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-mono-num font-bold text-2xl">{formatEUR(g.current_amount || 0)}</div>
                  <div className="text-sm text-stone-500 font-mono-num">/ {formatEUR(g.target_amount)}</div>
                </div>
                <Progress value={pct} className="h-2 [&>div]:bg-blue-500" />
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-stone-500">{pct.toFixed(0)}% atteint</div>
                  <div className="flex gap-2">
                    {[10, 50, 100].map((v) => (
                      <button key={v} onClick={() => addFunds(g, v)}
                              className="text-xs px-2.5 py-1 rounded-lg bg-stone-100 hover:bg-stone-200 font-medium transition-colors"
                              data-testid={`goal-add-${v}-${g.id}`}>
                        +{v}€
                      </button>
                    ))}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
